/* oxlint-disable typescript/no-explicit-any -- schema library requires flexible any types */
/* oxlint-disable typescript/no-unsafe-argument, typescript/no-unsafe-assignment, typescript/no-unsafe-call, typescript/no-unsafe-member-access, typescript/no-unsafe-return -- schema library uses any types throughout */
import * as errors from './errors'
import * as types from './types'
import * as util from './util'
import { describe, withField } from './descriptor'

// Shared init logic extracted so OptionalSchema/NullableSchema (defined below)
// can call it without a circular import — they live in this file alongside BaseSchema.
function _initBase(inst: any, def: types.SchemaTypeDef) {
  inst._zod = inst._zod ?? ({} as any)
  inst._zod.def = def
  inst._zod.bag = inst._zod.bag ?? {}
  inst._zod.meta = def.meta
  inst._zod.traits ??= new Set()
  inst.optional = () => optional(inst)
  inst.nullable = () => nullable(inst)

  const checks = [...(def.checks ?? [])]

  const runChecks = (
    payload: types.ParsePayload,
    checks: types.Check[],
    ctx: types.ParseContext,
  ): util.MaybeAsync<types.ParsePayload> => {
    let isAborted = util.aborted(payload)
    let asyncResult: Promise<unknown> | undefined

    for (const ch of checks) {
      if (isAborted) continue

      const currLen = payload.issues.length
      const result = ch._zod.check(payload)

      if (result instanceof Promise) {
        if (ctx.async === false) throw new types.AsyncError()
        // oxlint-disable-next-line no-loop-func, promise/always-return -- const bindings are block-scoped; void side-effect
        asyncResult = (asyncResult ?? Promise.resolve()).then(async () => {
          await result
          const nextLen = payload.issues.length
          if (nextLen === currLen) return
          if (!isAborted) isAborted = util.aborted(payload, currLen)
        })
      } else {
        const nextLen = payload.issues.length
        if (nextLen === currLen) continue
        if (!isAborted) isAborted = util.aborted(payload, currLen)
      }
    }

    if (asyncResult) return asyncResult.then(() => payload)
    return payload
  }

  if (checks.length === 0) {
    inst._zod.deferred ??= []
    inst._zod.deferred.push(() => {
      inst._zod.run = inst._zod.parse
    })
  } else {
    for (const ch of checks) {
      for (const fn of ch._zod.onattach) {
        fn(inst)
      }
    }

    inst._zod.run = (payload: types.ParsePayload, ctx: types.ParseContext) => {
      const result = inst._zod.parse(payload, ctx)
      if (result instanceof Promise) {
        if (ctx.async === false) throw new types.AsyncError()
        return result.then((result) => runChecks(result, checks, ctx))
      }
      return runChecks(result, checks, ctx)
    }
  }

  inst['~standard'] = {
    validate: (value: unknown) => {
      const result = inst.safeParse(value, { async: true })
      if (result instanceof Promise) {
        return result.then((r: any) => r.success ? { value: r.data } : { issues: r.error.issues })
      }
      return result.success ? { value: result.data } : { issues: result.error.issues }
    },
    vendor: 'w/schema',
    version: 1 as const,
  }

  inst.parse = (data: unknown, params?: types.ParseContext) => {
    const ctx: types.ParseContext = { async: false, ...params }
    const errorMap =
      params?.error ??
      (typeof def.error === 'function' && def.error.length === 2
        ? (def.error as errors.ErrorMap)
        : undefined)

    const payload = inst._zod.run({ value: data, issues: [] }, ctx)

    if (payload instanceof Promise) throw new types.AsyncError()
    if (payload.issues.length > 0)
      throw new errors.SchemaError(payload.issues, errorMap)
    return payload.value
  }

  inst.safeParse = (data: unknown, params?: types.ParseContext) => {
    const ctx: types.ParseContext = { async: false, ...params }
    const errorMap =
      params?.error ??
      (typeof def.error === 'function' && def.error.length === 2
        ? (def.error as errors.ErrorMap)
        : undefined)

      const payload = inst._zod.run({ value: data, issues: [] }, ctx)

      if (payload instanceof Promise) {
        if (ctx.async === false) throw new types.AsyncError()
        return payload.then((p: types.ParsePayload) => {
          if (p.issues.length > 0)
            return {
              success: false as const,
              error: new errors.SchemaError(p.issues, errorMap),
            }
          return { success: true as const, data: p.value }
        }) as any
      }

      if (payload.issues.length > 0)
        return {
          success: false as const,
          error: new errors.SchemaError(payload.issues, errorMap),
        }
      return { success: true as const, data: payload.value }
  }

  inst.meta = (metadata: Record<string, any>) => {
    return new inst._zod.constr({ ...def, meta: { ...def.meta, ...metadata } })
  }

  inst.field = (tag: number) => withField(inst, tag)
  inst.describe = () => describe(inst)
}

// --- OptionalSchema ---

export interface OptionalSchemaDef<T extends types.Schema>
  extends types.SchemaTypeDef {
  type: 'optional'
  schema: T
}

export interface OptionalSchemaInternals<
  T extends types.Schema,
> extends types.SchemaInternals<
  types.output<T> | undefined,
  types.input<T> | undefined
> {
  def: OptionalSchemaDef<T>
  optin: 'optional' | 'defaulted'
  optout: 'optional'
}

export interface OptionalSchema<T extends types.Schema> extends types.Schema<
  types.output<T> | undefined,
  types.input<T> | undefined,
  OptionalSchemaInternals<T>
> {
  meta(metadata: Record<string, any>): this
  describe(): types.SchemaDescriptor
  field(tag: number): this
  parse(data: unknown, params?: types.ParseContext): types.output<T> | undefined
  safeParse(
    data: unknown,
    params?: types.ParseContext,
  ):
    | { success: true; data: types.output<T> | undefined }
    | { success: false; error: errors.SchemaError }
  unwrap(): T
}

export const OptionalSchema: types.$constructor<
  OptionalSchema<any>,
  OptionalSchemaDef<any>
> = types.$constructor(
  'OptionalSchema',
  (inst: OptionalSchema<any>, def: OptionalSchemaDef<any>) => {
    _initBase(inst, def)

    inst._zod.optin = def.schema._zod.optin === 'defaulted' ? 'defaulted' : 'optional'
    inst._zod.optout = 'optional'

    inst._zod.parse = (payload, ctx) => {
      if (payload.value === undefined) {
        if (def.schema._zod.optin !== 'defaulted') return payload
        const result = def.schema._zod.run({ value: undefined, issues: [] }, ctx)
        const finish = (r: types.ParsePayload) => {
          payload.value = r.issues.length > 0 ? undefined : r.value
          return payload
        }
        if (result instanceof Promise) return result.then(finish)
        return finish(result)
      }
      return def.schema._zod.run(payload, ctx)
    }

    inst.unwrap = () => def.schema
  },
)

export function optional<T extends types.Schema>(schema: T): OptionalSchema<T> {
  return new OptionalSchema({ type: 'optional', schema })
}

// --- NullableSchema ---

export interface NullableSchemaDef<T extends types.Schema>
  extends types.SchemaTypeDef {
  type: 'nullable'
  schema: T
}

export interface NullableSchemaInternals<
  T extends types.Schema,
> extends types.SchemaInternals<types.output<T> | null, types.input<T> | null> {
  def: NullableSchemaDef<T>
  optin: T['_zod']['optin']
  optout: T['_zod']['optout']
}

export interface NullableSchema<T extends types.Schema> extends types.Schema<
  types.output<T> | null,
  types.input<T> | null,
  NullableSchemaInternals<T>
> {
  meta(metadata: Record<string, any>): this
  describe(): types.SchemaDescriptor
  field(tag: number): this
  parse(data: unknown, params?: types.ParseContext): types.output<T> | null
  safeParse(
    data: unknown,
    params?: types.ParseContext,
  ):
    | { success: true; data: types.output<T> | null }
    | { success: false; error: errors.SchemaError }
  unwrap(): T
}

export const NullableSchema: types.$constructor<
  NullableSchema<any>,
  NullableSchemaDef<any>
> = types.$constructor(
  'NullableSchema',
  (inst: NullableSchema<any>, def: NullableSchemaDef<any>) => {
    _initBase(inst, def)

    inst._zod.optin = def.schema._zod.optin
    inst._zod.optout = def.schema._zod.optout

    inst._zod.parse = (payload, ctx) => {
      if (payload.value === null) return payload
      return def.schema._zod.run(payload, ctx)
    }

    inst.unwrap = () => def.schema
  },
)

export function nullable<T extends types.Schema>(schema: T): NullableSchema<T> {
  return new NullableSchema({ type: 'nullable', schema })
}

// --- BaseSchema ---

export interface BaseSchema<O = unknown, I = unknown> extends types.Schema<
  O,
  I
> {
  parse(data: unknown, params?: types.ParseContext): O
  safeParse(
    data: unknown,
    params?: types.ParseContext,
  ): { success: true; data: O } | { success: false; error: errors.SchemaError }
  meta(metadata: Record<string, any>): this
  describe(): types.SchemaDescriptor
  field(tag: number): this
  optional(): OptionalSchema<this>
  nullable(): NullableSchema<this>
}

export const BaseSchema: types.$constructor<BaseSchema, types.SchemaTypeDef> =
  types.$constructor<BaseSchema>(
    'BaseSchema',
    (inst, def: types.SchemaTypeDef) => {
      _initBase(inst, def)
    },
  )
